import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import {
  isBrownfieldTrigger,
  isResourceTrigger,
  isViolationTrigger,
  isFindingsTrigger,
  isIssueTrigger,
} from 'src/app/common/playbookUtils';
import { PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { NewResourceFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newResourceTriggerModel';
import { NewViolationFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newViolationTriggerModel';
import { NewFindingsFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newFindingTriggerModel';
import { LabelInclusionStatus } from 'src/app/models/api/playbooks/triggers/labelInclusionStatus.enum';
import { ViolationStatus } from 'src/app/models/api/violations.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { PlaybooksHttpServiceService } from 'src/app/services/httpServices/playbooksHttpService/playbooks-http-service.service';

@Component({
  selector: 'app-playbook-projection',
  templateUrl: './playbook-projection.component.html',
  styleUrls: ['./playbook-projection.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, LabelsProviderPipe, MatTooltipModule, MatProgressSpinnerModule],
})
export class PlaybookProjectionComponent {
  public integrationId = input<string>();
  public trigger = input<PlaybookTrigger>();
  public policyIds = input<string[]>();

  // Fields that make exploration unavailable for violation triggers
  private readonly VIOLATION_TRIGGER_BLOCKING_FIELDS = [
    'resourceSecurityRisk',
    'resourceBusinessCriticality',
    'resourceCreatedBy',
    'resourceLastModifiedBy',
    'resourceCreatedSince',
    'resourceCreatedUntil',
    'resourceFirstSeenSince',
    'resourceFirstSeenUntil',
    'labels',
  ];

  // Fields that make exploration unavailable for resource triggers
  private readonly RESOURCE_TRIGGER_BLOCKING_FIELDS = [
    'resourceTypes',
    'resourceStatus',
    'resourceSecurityRisk',
    'resourceBusinessCriticality',
    'resourceCreatedBy',
    'resourceLastModifiedBy',
    'resourceFirstSeenSince',
    'resourceFirstSeenUntil',
  ];

  public hasAllRequiredInputs = computed(() => this.integrationId() && this.trigger() && this.policyIds()?.length);
  public isBrownfieldTrigger = computed(() => this.trigger() && isBrownfieldTrigger(this.trigger()!.triggerType));
  public isViolationTrigger = computed(() => this.trigger() && isViolationTrigger(this.trigger()!.triggerType));
  public isFindingsTrigger = computed(() => this.trigger() && isFindingsTrigger(this.trigger()!.triggerType));
  public isExplorationAvailable = computed(() => {
    const trigger = this.trigger()!;
    const triggerType = trigger.triggerType;

    if (isViolationTrigger(triggerType)) {
      return !this.hasAnyBlockingFields(
        trigger.configuration as NewViolationFoundTriggerConfigModel,
        this.VIOLATION_TRIGGER_BLOCKING_FIELDS
      );
    }

    if (isResourceTrigger(triggerType)) {
      return !this.hasAnyBlockingFields(
        trigger.configuration as NewResourceFoundTriggerConfigModel,
        this.RESOURCE_TRIGGER_BLOCKING_FIELDS
      );
    }

    if (isFindingsTrigger(triggerType)) {
      return false;
    }

    return true;
  });

  public projectionCount = signal<number | null>(null);
  public violationsCount = signal<number | null>(null);
  public hygieneCount = signal<number | null>(null);
  public isLoading = signal(false);

  private hasAnyBlockingFields(
    configuration:
      | NewViolationFoundTriggerConfigModel
      | NewResourceFoundTriggerConfigModel
      | NewFindingsFoundTriggerConfigModel,
    blockingFields: readonly string[]
  ): boolean {
    return blockingFields.some((field) => {
      const value = configuration[field as keyof typeof configuration];
      return value != undefined && Array.isArray(value) && (value as unknown[]).length > 0;
    });
  }

  constructor(
    private _router: Router,
    private _playbooksHttpService: PlaybooksHttpServiceService,
    private readonly _destroyRef: DestroyRef
  ) {
    effect(
      () => {
        const integrationId = this.integrationId();
        const trigger = this.trigger();
        const policyIds = this.policyIds();

        if (!integrationId || !trigger || !policyIds) {
          return;
        }

        if (isFindingsTrigger(trigger.triggerType) || isIssueTrigger(trigger.triggerType)) {
          this.projectionCount.set(null);
          this.isLoading.set(false);
          return;
        }

        this.isLoading.set(true);

        this._playbooksHttpService
          .getPlaybookProjection({
            integrationId,
            trigger,
            policyIds,
          })
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: (response) => {
              this.projectionCount.set(response.total);
              this.violationsCount.set(response.violationsCount || null);
              this.hygieneCount.set(response.hygieneCount || null);
              this.isLoading.set(false);
            },
          });
      },
      { allowSignalWrites: true }
    );
  }

  public onClick(): void {
    const integrationId = this.integrationId();
    const policyIds = this.policyIds();
    const trigger = this.trigger();

    if (!integrationId || !policyIds || !trigger) {
      return;
    }

    const targetRoutes = this._getTargetRouteForTrigger(trigger);
    const filters = this._getFiltersForTrigger(integrationId, policyIds, trigger);
    const filtersQuery = FiltersQueryTransformer.transformToQuery(filters);

    targetRoutes.forEach((route) => {
      const url = this._router
        .createUrlTree(['app', route], {
          queryParams: { q: filtersQuery },
        })
        .toString();

      window.open(url, '_blank');
    });
  }

  private _getFiltersForTrigger(
    integrationId: string,
    policyIds: string[],
    trigger: PlaybookTrigger
  ): ISelectedFilters {
    if (isViolationTrigger(trigger.triggerType)) {
      return this._convertToViolationFilters(
        integrationId,
        policyIds,
        trigger.configuration as NewViolationFoundTriggerConfigModel
      );
    }

    if (isResourceTrigger(trigger.triggerType)) {
      return this._convertToResourceFilters(
        integrationId,
        policyIds,
        trigger.configuration as NewResourceFoundTriggerConfigModel
      );
    }

    return this._convertToFindingsFilters(
      integrationId,
      policyIds,
      trigger.configuration as NewFindingsFoundTriggerConfigModel
    );
  }

  private _getTargetRouteForTrigger(trigger: PlaybookTrigger): string[] {
    if (isViolationTrigger(trigger.triggerType)) {
      const violationsCount = this.violationsCount();
      const hygieneCount = this.hygieneCount();
      const routes: string[] = [];

      if (violationsCount && violationsCount > 0) {
        routes.push('violations');
      }

      if (hygieneCount && hygieneCount > 0) {
        routes.push('hygiene');
      }

      // If no specific counts, fall back to violations
      return routes.length > 0 ? routes : ['violations'];
    }

    if (isResourceTrigger(trigger.triggerType)) {
      return ['inventory'];
    }

    return ['ai-findings'];
  }

  private _convertToViolationFilters(
    integrationId: string,
    policyIds: string[],
    configuration: NewViolationFoundTriggerConfigModel
  ): ISelectedFilters {
    return {
      integration: [integrationId],
      policy: policyIds,
      violationRule: configuration.ruleIds,
      severity: configuration.severity,
      type: configuration.resourceTypes,
      resourceStatus: configuration.resourceStatus,
      generativeAiOptions: configuration.resourceGenerativeAiOptions,
      ruleTag: configuration.ruleTags,
      violationStatus: [ViolationStatus.open],
      ...(configuration.labels?.inclusionStatus === LabelInclusionStatus.include && {
        label: configuration.labels.labelsIds,
      }),
    };
  }

  private _convertToResourceFilters(
    integrationId: string,
    policyIds: string[],
    configuration: NewResourceFoundTriggerConfigModel
  ): ISelectedFilters {
    return {
      integration: [integrationId],
      policy: policyIds,
      type: configuration.resourceTypes,
      status: configuration.resourceStatus,
      fromDate: configuration.resourceCreatedSince,
      toDate: configuration.resourceCreatedUntil,
      securityRisk: configuration.resourceSecurityRisk,
      businessCriticality: configuration.resourceBusinessCriticality,
      generativeAiOptions: configuration.resourceGenerativeAiOptions,
    };
  }

  private _convertToFindingsFilters(
    integrationId: string,
    policyIds: string[],
    configuration: NewFindingsFoundTriggerConfigModel
  ): ISelectedFilters {
    return {
      integration: [integrationId],
      policy: policyIds,
      violationRule: configuration.ruleIds,
      severity: configuration.ruleSeverity,
      aiService: configuration.aiServices,
    };
  }
}
